import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { userService, type UserListParams, type AddressListParams } from '@/api/services/userService'

// ─── User List ────────────────────────────────────────────────────────────────

export function useUsers(p: UserListParams, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['users', 'list', p],
    queryFn: () => userService.list(p),
    staleTime: 30_000,
    enabled: opts?.enabled ?? true,
  })
}

export function useTrainers(p: UserListParams) {
  return useUsers({ ...p, user_types: 'TRAINER' })
}

export function useStudents(p: UserListParams) {
  return useUsers({ ...p, user_types: 'STUDENT' })
}

// ─── Single User ──────────────────────────────────────────────────────────────

// Get user by ID via admin/user/get-user/ (mirrors old useGetUserDetails)
export function useUser(id: number) {
  return useQuery({
    queryKey: ['users', id],
    queryFn: () => userService.getById(id),
    enabled: !!id,
  })
}

// Get trainer details via admin/trainers/get-trainer/ (mirrors old useGetUserById)
export function useTrainer(id: number) {
  return useQuery({
    queryKey: ['users', 'trainer', id],
    queryFn: () => userService.getTrainerById(id),
    enabled: !!id,
  })
}

// Get user via assistant endpoint (mirrors old useGetAssistantUserDetails)
export function useAssistantUser(id: number) {
  return useQuery({
    queryKey: ['users', 'assistant', id],
    queryFn: () => userService.getAssistantUserById(id),
    enabled: !!id,
  })
}

// ─── Enums ────────────────────────────────────────────────────────────────────

// mirrors old useGetUserTypeEnum
export function useUserTypeEnum() {
  return useQuery({
    queryKey: ['enums', 'userType'],
    queryFn: () => userService.getUserTypeEnum(),
    staleTime: Infinity,
  })
}

// mirrors old useGetGenderEnum
export function useGenderEnum() {
  return useQuery({
    queryKey: ['enums', 'gender'],
    queryFn: () => userService.getGenderEnum(),
    staleTime: Infinity,
  })
}

// mirrors old useGetGearEnum
export function useGearEnum() {
  return useQuery({
    queryKey: ['enums', 'gear'],
    queryFn: () => userService.getGearEnum(),
    staleTime: Infinity,
  })
}

// ─── Mutations: User ──────────────────────────────────────────────────────────

export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => userService.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => userService.update(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useDeleteUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => userService.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

// mirrors old createTrainer
export function useCreateTrainer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => userService.createTrainer(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

// ─── Addresses ───────────────────────────────────────────────────────────────

// mirrors old useGetAddressList
export function useAddressList(p: AddressListParams) {
  return useQuery({
    queryKey: ['users', 'addresses', p],
    queryFn: () => userService.listAddresses(p),
    enabled: !!p.user_id,
    staleTime: 30_000,
  })
}

// mirrors old createNewAddressForUser + updateExistingUserAddress (same endpoint)
export function useSaveAddress() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => userService.saveAddress(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users', 'addresses'] }),
  })
}

// mirrors old deleteUserAddress
export function useDeleteAddress() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (addressId: number) => userService.deleteAddress(addressId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users', 'addresses'] }),
  })
}

// ─── User Documents ──────────────────────────────────────────────────────────

export function useUserDocumentList(params: Record<string, unknown>) {
  return useQuery({
    queryKey: ['users', 'documents', 'list', params],
    queryFn: () => userService.getDocumentList(params),
    staleTime: 30_000,
  })
}

export function useUserDocumentById(id: number) {
  return useQuery({
    queryKey: ['users', 'documents', id],
    queryFn: () => userService.getDocumentById(id),
    enabled: !!id,
  })
}

export function useSaveUserDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown> | FormData) => userService.createOrUpdateDocument(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users', 'documents'] }),
  })
}

export function useDeleteUserDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => userService.deleteDocumentById(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users', 'documents'] }),
  })
}

export function useDeleteAllUserDocuments() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) => userService.deleteAllDocumentsByUserId(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users', 'documents'] }),
  })
}

export function useApproveUserDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => userService.approveDocument(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users', 'documents'] }),
  })
}
